import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import Header from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useWidgetDownload } from "@/hooks/useWidgetDownload";
import type { Habit } from "@/context/SupabaseHabitContext";
import { createLocalDate } from "@/utils/dateUtils";
import { ArrowLeft, Copy, Smartphone, Apple } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];
const ROWS = 7;
const COLS = 6;

function buildWidgetGridData(habit: Habit): number[] {
  const completedDates = habit.completedDates ?? [];
  const today = new Date();
  const refDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const D = refDate.getDay();
  const gridData = new Array<number>(42).fill(0);
  for (const dateStr of completedDates) {
    const compDate = createLocalDate(dateStr);
    const daysAgo = Math.round((refDate.getTime() - compDate.getTime()) / (24 * 60 * 60 * 1000));
    if (daysAgo < 0 || daysAgo > 41) continue;
    const r = compDate.getDay();
    const weekNum = (daysAgo - D + r) / 7;
    if (weekNum % 1 !== 0 || weekNum < 0 || weekNum > 5) continue;
    const c = 5 - weekNum;
    gridData[r * 6 + c] = 1;
  }
  return gridData;
}

function WidgetPreview({ habit }: { habit: Habit }) {
  const gridData = useMemo(() => buildWidgetGridData(habit), [habit.completedDates, habit.id]);
  const todayRow = new Date().getDay();
  const todayCol = 5;
  const filled = habit.color?.filled ?? "hsl(266, 70%, 65%)";
  const empty = habit.color?.empty ?? "hsl(217, 33%, 17%)";

  return (
    <div
      className="inline-flex flex-col rounded-xl border border-border overflow-hidden shadow-lg"
      style={{ backgroundColor: "#020617" }}
    >
      <div className="flex items-stretch">
        <div className="flex flex-col justify-around py-1 pr-1 text-[10px] text-slate-500 font-medium">
          {DAY_LABELS.map((l) => (
            <span key={l} className="h-3 flex items-center justify-center w-3">
              {l}
            </span>
          ))}
        </div>
        <div className="grid grid-cols-6 gap-0.5 p-1">
          {Array.from({ length: ROWS * COLS }, (_, i) => {
            const r = Math.floor(i / COLS);
            const c = i % COLS;
            const isFilled = gridData[i] === 1;
            const isToday = r === todayRow && c === todayCol;
            return (
              <div
                key={i}
                className="w-3 h-3 rounded-sm flex-shrink-0"
                style={{
                  backgroundColor: isFilled ? filled : empty,
                  boxShadow: isToday ? `inset 0 0 0 2px ${filled}` : undefined,
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

const dummyHabit: Habit = {
  id: "",
  name: "",
  completedDates: [],
  color: { empty: "", filled: "", hover: "" },
  createdAt: "",
  active: true,
  isCollapsed: false,
  pinned: false,
};

export default function WidgetSetupPage() {
  const location = useLocation();
  const habit = (location.state?.habit as Habit | undefined) ?? dummyHabit;
  const [widgetCode, setWidgetCode] = useState<string | null>(null);
  const [codeLoading, setCodeLoading] = useState(true);
  const [copying, setCopying] = useState(false);

  const { getWidgetCode } = useWidgetDownload(habit);

  useEffect(() => {
    document.title = habit.id ? `Widget setup: ${habit.name} | Habitualy` : "Widget setup | Habitualy";
  }, [habit.id, habit.name]);

  useEffect(() => {
    if (!habit.id) return;
    getWidgetCode()
      .then((code) => setWidgetCode(code ?? null))
      .catch(() => setWidgetCode(null))
      .finally(() => setCodeLoading(false));
  }, [habit.id, getWidgetCode]);

  if (!habit.id) {
    return <Navigate to="/dashboard" replace />;
  }

  const copyWithExecCommand = (text: string): boolean => {
    try {
      const el = document.createElement("textarea");
      el.value = text;
      el.readOnly = true;
      el.style.position = "fixed";
      el.style.left = "-9999px";
      el.style.top = "0";
      el.style.fontSize = "16px";
      document.body.appendChild(el);
      el.focus();
      el.select();
      el.setSelectionRange(0, text.length);
      const ok = document.execCommand("copy");
      document.body.removeChild(el);
      return ok;
    } catch {
      return false;
    }
  };

  const handleCopyCode = () => {
    if (!widgetCode) {
      toast.error("Code not ready yet. Please wait.");
      return;
    }
    setCopying(true);
    const ok = copyWithExecCommand(widgetCode);
    setCopying(false);
    if (ok) {
      toast.success(`Code for "${habit.name}" copied to clipboard`);
    } else if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(widgetCode).then(
        () => toast.success(`Code for "${habit.name}" copied to clipboard`),
        () => toast.error("Failed to copy. Try selecting the code below.")
      );
    } else {
      toast.error("Failed to copy. Try selecting the code below.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 py-8">
        <div className="container max-w-2xl">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to dashboard
          </Link>

          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Smartphone className="h-6 w-6" />
                  Scriptable widget
                </h1>
                <Badge variant="secondary" className="gap-1 font-normal">
                  <Apple className="h-3 w-3" />
                  iOS only (for now)
                </Badge>
              </div>
              <p className="text-muted-foreground mt-1">
                Show &quot;{habit.name}&quot; on your iPhone home screen.
              </p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>What it looks like</CardTitle>
                <p className="text-sm text-muted-foreground font-normal">
                  A small grid on your home screen: each column is a week, each row is a day (Sun–Sat). Filled squares are days you completed &quot;{habit.name}&quot;; the purple outline is today.
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center p-4 rounded-lg bg-muted/30">
                  <WidgetPreview habit={habit} />
                </div>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Preview · Your widget will use the smallest home screen size.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Instructions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ol className="list-decimal list-inside space-y-3 text-sm text-muted-foreground">
                  <li>
                    Install <strong className="text-foreground">Scriptable</strong> from the App Store (free).
                  </li>
                  <li>
                    Open Scriptable and tap the <strong className="text-foreground">+</strong> button to create a new script.
                  </li>
                  <li>
                    Tap <strong className="text-foreground">Copy code</strong> below to copy the widget code for &quot;{habit.name}&quot;. In Scriptable, paste the code (replace any existing content).
                  </li>
                  <li>
                    Run the script (play button), then choose <strong className="text-foreground">Add to Home Screen</strong>. When picking the widget size, select the <strong className="text-foreground">smallest</strong> widget.
                  </li>
                </ol>
                <Button
                  onClick={handleCopyCode}
                  disabled={codeLoading || copying}
                  className="w-full sm:w-auto"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  {codeLoading ? "Preparing…" : copying ? "Copying…" : `Copy code for "${habit.name}"`}
                </Button>
                {!codeLoading && widgetCode && (
                  <p className="text-xs text-muted-foreground mt-2">
                    If copy fails on your device, tap and hold in the code box below to select all, then copy.
                  </p>
                )}
                {!codeLoading && widgetCode && (
                  <textarea
                    readOnly
                    value={widgetCode}
                    className="w-full h-48 p-3 mt-2 font-mono text-xs bg-muted rounded-md border overflow-auto resize-y"
                    onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                    aria-label="Widget code"
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
